import { isValid, parseISO, startOfDay } from 'date-fns';

const MILLISECONDS_IN_HOUR = 3600000;
const MILLISECONDS_IN_MINUTE = 60000;
const dateRegex = /^-?(?:(\d{3})|(\d{2})(?:-?(\d{2}))?|W(\d{2})(?:-?(\d{1}))?|)/;
const timeRegex = /^T?(\d{2}(?:[.,]\d*)?)(?::?(\d{2}(?:[.,]\d*)?))?(?::?(\d{2}(?:[.,]\d*)?))?$/;
const timezoneRegex = /(Z|([+-])(\d{2}):?(\d{2}))+$/;

/**
 * Parses a time string in various formats and returns a Date object.
 *
 * @param time - The time string to parse
 * @param referenceDate
 * @returns A Date object representing the parsed time
 * @throws Error if the time string is invalid or contains time fractions
 */
export function parseIsoTime(time: string, referenceDate: Date = new Date()): Date {
  if (!time) {
    throw new Error('Time string cannot be null or undefined');
  }

  // Try parsing as a full ISO string
  if (time.match(/[T ]/)) {
    const dateResult = parseISO(time);
    if (isValid(dateResult)) {
      return dateResult;
    }
  }

  const captures = time.replace(timezoneRegex, '').match(timeRegex);

  if (!captures) {
    return new Date(NaN);
  }

  const hours = parseTimeUnit(captures[1]);
  const minutes = parseTimeUnit(captures[2]);
  const seconds = parseTimeUnit(captures[3]);

  if (!validateTime(hours, minutes, seconds)) {
    throw new Error('Invalid iso time format.');
  }

  // Try parsing by combining today's date with the time
  const unixDate = startOfDay(referenceDate).getTime();
  const unixTime = hours * MILLISECONDS_IN_HOUR + minutes * MILLISECONDS_IN_MINUTE + seconds * 1000;

  const result = new Date(unixDate + unixTime);
  if (isValid(result)) {
    return result;
  }
  return new Date(NaN);
}

function parseTimeUnit(value: string) {
  return (value && parseFloat(value.replace(',', '.'))) || 0;
}

function validateTime(hours: number, minutes: number, seconds: number) {
  if (hours === 24) {
    return minutes === 0 && seconds === 0;
  }

  return seconds >= 0 && seconds < 60 && minutes >= 0 && minutes < 60 && hours >= 0 && hours < 25;
}
